import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import JSZip from "jszip";
import * as api from "../../api/client";
import type { ApiCtx } from "../../api/client";
import type { CloudConfig } from "../../api/types";
import { toast } from "../../notify";
import { texte } from "../../texte";

/**
 * Konfig-Tab (nur Admin). Liest den aktuellen Stand über configure(ctx, {}, "admin").
 */
export function Config({ ctx }: { ctx: ApiCtx }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["config", ctx.apiUrl, ctx.token, ctx.workspace, "admin"],
    queryFn: () => api.configure(ctx, {}, "admin"),
  });
  const readmeQuery = useQuery({
    queryKey: ["readme", ctx.apiUrl, ctx.token, ctx.workspace],
    queryFn: () => api.getReadme(ctx),
  });

  const [draft, setDraft] = useState<CloudConfig | null>(null);
  const [readmeDraft, setReadmeDraft] = useState<string>("");
  const [showInfo, setShowInfo] = useState<any>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (query.data?.config) {
      setDraft({ expert_enabled: false, ...query.data.config } as CloudConfig);
    }
  }, [query.data]);

  useEffect(() => {
    if (readmeQuery.data?.readme !== undefined) {
      setReadmeDraft(readmeQuery.data.readme);
    }
  }, [readmeQuery.data]);

  if (!draft) return null;

  async function apply() {
    if (!draft) return;
    const res = await api.configure(ctx, draft, "admin");
    if (res.code < 0) {
      toast(texte.errors.generic, res.msg ?? texte.errors.generic);
      return;
    }
    toast(texte.config.saved, texte.config.savedText, true);
    queryClient.invalidateQueries({ queryKey: ["config", ctx.apiUrl, ctx.token, ctx.workspace] });
  }

  async function handleExport() {
    if (!draft) return;
    setExporting(true);
    try {
      const zip = new JSZip();
      
      const cleanDraft = { ...draft };
      delete cleanDraft.expert_enabled;
      zip.file("config.js", `window.config = ${JSON.stringify(cleanDraft, null, 2)};`);
      
      zip.file("readme.md", readmeDraft || "");
      
      const pullRes = await api.pull(ctx);
      if (pullRes.code === 0 && pullRes.documents) {
        for (const doc of pullRes.documents) {
          if (doc.doc_id === "goal") {
            zip.file("goal.md", doc.content || "");
          } else {
            const name = doc.filename || doc.doc_id;
            if (doc.content_base64) {
              zip.file(name, doc.content_base64, { base64: true });
            } else {
              zip.file(name, doc.content || "");
            }
          }
        }
      }
      
      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${ctx.workspace}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast("Export erfolgreich", `Arbeitsbereich wurde als ZIP heruntergeladen.`, true);
    } catch (err: any) {
      console.error("[Export] failed", err);
      toast(texte.errors.generic, `Export failed: ${err.message || err}`);
    } finally {
      setExporting(false);
    }
  }

  async function clearGutscheine() {
    if (!draft) return;
    if (!window.confirm(`Alle Gutscheine wirklich löschen?`)) return;
    
    const newDraft = { ...draft };
    newDraft.gutscheine = {};
    
    const res = await api.configure(ctx, { gutscheine: {} }, "admin");
    if (res.code === 0) {
      setDraft(newDraft as CloudConfig);
      toast("Gutscheine gelöscht", `Alle Codes wurden entfernt.`, true);
      queryClient.invalidateQueries({ queryKey: ["config", ctx.apiUrl, ctx.token, ctx.workspace] });
    } else {
      toast(texte.errors.generic, res.msg ?? texte.errors.generic);
    }
  }

  async function generateBulkGutscheine() {
    if (!draft) return;
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const newGutscheine: Record<string, number> = {};
    for (let j = 0; j < 10; j++) {
      let code = "";
      for (let i = 0; i < 6; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
      newGutscheine[code] = 150000;
    }
    
    const newDraft = { ...draft };
    newDraft.gutscheine = { ...(newDraft.gutscheine || {}), ...newGutscheine };
    
    const res = await api.configure(ctx, { gutscheine: newDraft.gutscheine }, "admin");
    if (res.code === 0) {
      setDraft(newDraft as CloudConfig);
      toast("Gutscheine erstellt", `10 neue Codes (à 150.000) generiert.`, true);
      queryClient.invalidateQueries({ queryKey: ["config", ctx.apiUrl, ctx.token, ctx.workspace] });
    } else {
      toast(texte.errors.generic, res.msg ?? texte.errors.generic);
    }
  }

  function updateDraft(key: keyof CloudConfig, val: any) {
    setDraft((d) => d ? { ...d, [key]: val } : null);
  }

  function renderSelect(k: keyof CloudConfig, options: any[]) {
    const d = draft!;
    const isModel = k !== "language";
    
    const dropdown = (
      <select
        className="input"
        style={isModel ? { flex: 1 } : {}}
        value={String(d[k] ?? "")}
        onChange={(e) => updateDraft(k, e.target.value)}
      >
        {isModel ? (
          <>
            {String(d[k]) && !options.some((opt) => opt.ID === String(d[k])) && (
              <option value={String(d[k])}>{String(d[k])}</option>
            )}
            {options.map((opt) => (
              <option key={opt.ID} value={opt.ID}>
                {opt.label}
              </option>
            ))}
          </>
        ) : (
          <>
            {!options.includes(String(d[k])) && <option value={String(d[k])}>{String(d[k])}</option>}
            {options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </>
        )}
      </select>
    );

    if (isModel) {
      return (
        <div style={{ display: "flex", gap: "6px", alignItems: "center", width: "100%" }}>
          {dropdown}
          <button
            type="button"
            className="btn ghost"
            style={{
              padding: "2px 6px",
              fontSize: "0.85rem",
              minWidth: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1
            }}
            onClick={() => {
              const selectedVal = d[k];
              const currentModel = options.find((m) => m.ID === selectedVal);
              if (currentModel) {
                setShowInfo(currentModel);
              }
            }}
            title="Modell-Details anzeigen"
          >
            ?
          </button>
        </div>
      );
    }

    return dropdown;
  }

  function renderInput(k: keyof CloudConfig, type: "text" | "number" | "boolean" = "text", step?: number) {
    const d = draft!;
    if (type === "boolean") {
      return (
        <label className="switch">
          <input
            type="checkbox"
            checked={!!(d[k] as any)}
            onChange={(e) => updateDraft(k, e.target.checked)}
          />
          <span className="tr" />
        </label>
      );
    }
    return (
      <input
        type={type}
        className="input"
        step={step}
        value={(d[k] as any) ?? ""}
        onChange={(e) => {
          if (type === "number") {
             const num = Number(e.target.value);
             updateDraft(k, isNaN(num) ? e.target.value : num);
          } else {
             updateDraft(k, e.target.value);
          }
        }}
      />
    );
  }

  const allModels = window.config?.models || [];

  return (
    <div className="config-view">
      <div className="cfg-grid">
        <div className="cfg-group-card">
          <h3>Allgemeine Einstellungen</h3>
          <div className="cfg-table">
            <div className="rowi"><label>Prompt-Sprache</label>{renderSelect("language", ["de", "en"])}</div>
            <div className="rowi"><label>Hauptmodell</label>{renderSelect("model", allModels)}</div>
            <div className="rowi"><label>Subagent-Modell</label>{renderSelect("subagent_model", allModels)}</div>
            <div className="rowi"><label>Python zulassen</label>{renderInput("python", "boolean")}</div>
            <div className="rowi"><label>Web-Suche zulassen</label>{renderInput("web", "boolean")}</div>
            <div className="rowi"><label>Bilder erkennen</label>{renderInput("image", "boolean")}</div>
            <div className="rowi"><label>Bild-Modell</label>{renderSelect("image_model", allModels)}</div>
            <div className="rowi"><label>Experten-Modus aktiv</label>{renderInput("expert_enabled", "boolean")}</div>
          </div>
        </div>

        <div className="cfg-group-card">
          <h3>Leitplanken & Limits</h3>
          <div className="cfg-table">
            <div className="rowi"><label>Budget (Tokens)</label>{renderInput("budget", "number", 50000)}</div>
            <div className="rowi"><label>Max. Iterationen</label>{renderInput("max_iterations", "number")}</div>
            <div className="rowi"><label>Max. parallele Tasks</label>{renderInput("max_parallel", "number")}</div>
            <div className="rowi"><label>Max. Retries</label>{renderInput("max_retries", "number")}</div>
            <div className="rowi"><label>Max. Refinements</label>{renderInput("max_refinements", "number")}</div>
          </div>
        </div>

        <div className="cfg-group-card">
          <h3>Plan-Modus</h3>
          <div className="cfg-table">
            <div className="rowi"><label>Planer aktiv (plan)</label>{renderInput("plan", "boolean")}</div>
            <div className="rowi"><label>Planer-Modell</label>{renderSelect("planner_model", allModels)}</div>
            <div className="rowi"><label>Max. Plan-Tiefe</label>{renderInput("max_plan_depth", "number")}</div>
          </div>
        </div>

        <div className="cfg-group-card">
          <h3>Kritik & Reflexion</h3>
          <div className="cfg-table">
            <div className="rowi"><label>Kritik aktiv</label>{renderInput("critique", "boolean")}</div>
            <div className="rowi"><label>Kritiker-Modell</label>{renderSelect("critique_model", allModels)}</div>
            <div className="rowi"><label>Kritik-Runden</label>{renderInput("critique_rounds", "number")}</div>
          </div>
        </div>

        <div className="cfg-group-card" style={{ gridColumn: "1 / -1" }}>
          <h3>Workspace-Anleitung (readme.md)</h3>
          <p className="hint">
            Diese Anleitung wird Schülerinnen und Schülern als Begrüßung (Splash) angezeigt, bevor der Workspace geladen wird.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
            <textarea
              className="input"
              style={{ fontFamily: "monospace", minHeight: 180, width: "100%", boxSizing: "border-box" }}
              value={readmeDraft}
              onChange={(e) => setReadmeDraft(e.target.value)}
              placeholder="# Anleitung für diesen Workspace..."
            />
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button className="btn small" onClick={async () => {
                const res = await api.saveReadme(ctx, readmeDraft, "admin");
                if (res.code === 0) {
                  toast("Readme gespeichert", "Die Anleitung wurde aktualisiert.", true);
                  queryClient.invalidateQueries({ queryKey: ["readme", ctx.apiUrl, ctx.token, ctx.workspace] });
                } else {
                  toast(texte.errors.generic, res.msg ?? texte.errors.generic);
                }
              }}>
                Readme Speichern
              </button>
              <input
                type="file"
                accept=".md,text/markdown,text/plain"
                style={{ display: "none" }}
                id="readme-upload"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = (evt) => {
                    const text = evt.target?.result as string;
                    setReadmeDraft(text);
                  };
                  reader.readAsText(file);
                }}
              />
              <label htmlFor="readme-upload" className="btn small ghost" style={{ cursor: "pointer", display: "inline-block", margin: 0 }}>
                Datei hochladen (.md)
              </label>
            </div>
          </div>
        </div>

        <div className="cfg-group-card" style={{ gridColumn: "1 / -1" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Gutscheine Verwalten</h3>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn small" onClick={clearGutscheine}>Alle löschen</button>
              <button className="btn small primary" onClick={generateBulkGutscheine}>10x 150k erstellen</button>
            </div>
          </div>
          {(!draft.gutscheine || Object.keys(draft.gutscheine).length === 0) ? (
            <p className="hint">Keine aktiven Gutscheine.</p>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
              {Object.keys(draft.gutscheine).map((code) => (
                <div key={code} style={{ 
                  background: "var(--surface)", 
                  padding: "8px 12px", 
                  borderRadius: 6, 
                  border: "1px solid var(--line)",
                  fontFamily: "monospace",
                  fontSize: 15,
                  userSelect: "all",
                  cursor: "copy"
                }}>
                  {code}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="cfg-group-card" style={{ gridColumn: "1 / -1" }}>
          <h3>Arbeitsbereich exportieren</h3>
          <p className="hint" style={{ marginBottom: 12 }}>
            Exportiert diesen Arbeitsbereich als ZIP-Datei. Diese enthält die Anleitung (readme.md),
            die Konfiguration (config.js), den Aufgabentext (goal.md) und alle weiteren Dateien dieses Arbeitsbereichs.
          </p>
          <button className="btn" onClick={handleExport} disabled={exporting}>
            {exporting ? "Wird exportiert..." : "Arbeitsbereich exportieren (ZIP)"}
          </button>
        </div>
      </div>

      <p className="hint" style={{ margin: "14px 0" }}>
        {texte.config.applyHint}
      </p>
      <button className="btn primary" onClick={apply}>
        {texte.config.apply}
      </button>

      {showInfo && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10000,
          padding: "16px"
        }} onClick={() => setShowInfo(null)}>
          <div style={{
            backgroundColor: "var(--surface)",
            color: "var(--c-text)",
            border: "1px solid var(--line)",
            borderRadius: "12px",
            width: "100%",
            maxWidth: "480px",
            padding: "24px",
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h3 style={{ margin: 0, fontSize: "1.15rem" }}>{showInfo.label}</h3>
              <button type="button" className="btn ghost" style={{ minWidth: 0, padding: "4px 8px" }} onClick={() => setShowInfo(null)}>✕</button>
            </div>
            <div style={{ fontSize: "0.95rem", lineHeight: "1.5", marginBottom: "16px", whiteSpace: "pre-wrap" }}>
              {showInfo.description}
            </div>
            <div style={{ fontSize: "0.85rem", color: "var(--muted)", borderTop: "1px solid var(--line)", paddingTop: "12px" }}>
              <div><strong>Anbieter (Vendor):</strong> {showInfo.provider}</div>
              <div style={{ marginTop: "4px" }}><strong>Kosten (Cost):</strong> {showInfo.cost}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
